import React, { useMemo } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { ReactComponent as IgnoreTerm } from "icons/discovery/IgnoreTerm.svg";
import { ReactComponent as AddToDictionary } from "icons/discovery/AddToDictionary.svg";
import { getActionButtonsCoords } from "layout/discovery/helpers";
import { ACTION_BUTTONS_WIDTH, PLACEMENTS } from "constants/discovery";
import { addToIgnoreList } from "slices/discovery/actions/actionButtons";
import {
  setByContentHoveredTerm,
  showAddToDictPanel,
} from "slices/discovery/discoverySlice";
import { modalTypes } from "constants/types";
import { showModal } from "slices/app/actions/modals";
import { hasRole } from "utils";
import { USER_ROLES } from "constants/app";
import {
  tooltipPlacement,
  TooltipTrigger,
} from "@visiblethread/react-components/dist/components/Tooltip";

const ActionButtons = ({
  terms,
  term,
  placement,
  parentRef,
  style,
  alignBottom,
}) => {
  const dispatch = useDispatch();
  const hasIgnoreTermPermissions = hasRole(USER_ROLES.discoverySettings);
  const hasEditDictionaryPermissions = hasRole(USER_ROLES.dictionary);

  const { top, left } = useMemo(
    () => getActionButtonsCoords(parentRef, placement, alignBottom),
    []
  );

  const handleAddToDictionary = () => {
    dispatch(setByContentHoveredTerm(null));
    dispatch(showAddToDictPanel({ terms, term }));
  };

  const handleIgnoreTerm = () => {
    if (terms.length > 1) {
      dispatch(showModal(modalTypes.IGNORE_TERM_CONFIRM, { terms, term }));
    } else {
      dispatch(addToIgnoreList(terms));
    }
  };

  const actions = [
    {
      Icon: AddToDictionary,
      tooltipText: hasEditDictionaryPermissions
        ? "Add to Dictionary"
        : "You do not have permission to edit dictionaries",
      disabled: !hasEditDictionaryPermissions,
      onClick: handleAddToDictionary,
    },
    {
      Icon: IgnoreTerm,
      tooltipText: hasIgnoreTermPermissions
        ? "Ignore Term"
        : "You do not have permission to edit Discovery Ignore List",
      disabled: !hasIgnoreTermPermissions,
      onClick: handleIgnoreTerm,
    },
  ];

  return ReactDOM.createPortal(
    <div
      style={{ ...style, top, left, width: ACTION_BUTTONS_WIDTH }}
      className="discovery__action_buttons"
      data-testid="action-buttons"
    >
      {actions.map(({ Icon, tooltipText, disabled, onClick }) => (
        <TooltipTrigger
          content={tooltipText}
          delay={250}
          placement={tooltipPlacement.top}
        >
          <button type="button" disabled={disabled} onClick={onClick}>
            <Icon />
          </button>
        </TooltipTrigger>
      ))}
    </div>,
    document.body
  );
};

ActionButtons.defaultProps = {
  style: {},
  alignBottom: false,
};

ActionButtons.propTypes = {
  // All terms for which action should be applied
  terms: PropTypes.arrayOf(PropTypes.string).isRequired,
  // Term where action buttons where called from
  term: PropTypes.string,
  placement: PropTypes.oneOf([PLACEMENTS.LEFT, PLACEMENTS.RIGHT]).isRequired,
  parentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.oneOf(PropTypes.string, PropTypes.object),
  alignBottom: PropTypes.bool,
};

export default ActionButtons;
